import { ForwardRecipientsSchema } from "../../src/inbox-v2/forward-contract.ts";

/** What the rep typed on the To, Cc and Bcc lines: comma-separated addresses. */
export type RecipientText = { readonly to: string; readonly cc: string; readonly bcc: string };

/**
 * One line's entries, split on the commas between them. A comma inside a quoted name or inside
 * angle brackets belongs to that entry, so `"Doe, Jane" <jane@x.invalid>` stays one.
 */
function entries(value: string): string[] {
  const found: string[] = [];
  let current = "";
  let quoted = false;
  let angled = false;
  for (const character of value) {
    if (character === '"' && !angled) quoted = !quoted;
    if (character === "<" && !quoted) angled = true;
    if (character === ">" && !quoted) angled = false;
    if (character === "," && !quoted && !angled) {
      found.push(current);
      current = "";
    } else current += character;
  }
  return [...found, current].map((entry) => entry.trim()).filter(Boolean);
}

/** The address an entry names: the part in angle brackets when there is one, as mail clients write. */
function address(entry: string): string {
  return /<([^<>]*)>\s*$/u.exec(entry)?.[1]?.trim() ?? entry;
}

/** The lines parsed as the server parses a forward's or a reply's recipients: one schema for both. */
export function parseRecipientText(text: RecipientText) {
  const addresses = (value: string) => entries(value).map(address);
  return ForwardRecipientsSchema.safeParse({
    to: addresses(text.to),
    cc: addresses(text.cc),
    bcc: addresses(text.bcc),
  });
}

/** An address as a line shows it: `Name <address>` where a name is known, quoted when it needs to be. */
export function recipientEntry(address: string, name: string | null | undefined): string {
  const plain = name?.trim();
  if (!plain || plain.toLowerCase() === address.toLowerCase()) return address;
  const shown = /[",<>]/u.test(plain) ? `"${plain.replaceAll('"', "")}"` : plain;
  return `${shown} <${address}>`;
}
